import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSideDrawer } from '../../HBSideDrawer/HBSideDrawer';
import HBLogo from '../../../components/HBLogo/HBLogo';
import { ArrowRightLineThin16 } from '@honeybook/icons';
import HBIconButton from '../../HBIconButton/HBIconButton';
import { useSideNavContext } from '../context/SideNavProvider';
import { ISideNavTheme } from '../context/useCreateTheme';
import colors from '../../../styles/colors';

export interface ISideNavHeaderProps {
    logoHref?: string;
    autoCollapseWidth?: number;
    onLogoClick?: () => void;
    /** When true, hides the pin/unpin button. Useful when an adjacent panel
     *  leaves no room for a pinned sidenav (e.g. an AI chat side panel). */
    hidePinButton?: boolean;
}

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 15px 12px 4px 13px;
    align-items: center;
    gap: 16px;
`;

const PinButton = styled(HBIconButton)<{
    $theme: ISideNavTheme;
    isPinned: boolean;
    isHovered: boolean;
    tabIndex: string;
}>`
    ${({ isPinned }) => isPinned && `transform: rotate(180deg)`};
    &:focus-visible {
        outline: 2px solid var(--blue-20597-eff, #597eff);
    }
    opacity: ${({ isHovered }) => (isHovered ? 1 : 0)};
    transition: opacity 200ms ease-in-out;

    && svg * {
        fill: ${({ $theme }) => $theme.itemTextColor};
    }

    &&:hover {
        background-color: ${({ $theme }) => $theme.itemBackgroundColorHover};
        & svg * {
            fill: ${({ $theme }) => $theme.itemTextColor};
        }
    }
    &&:active {
        background-color: ${({ $theme }) => $theme.itemBackgroundColorActive};
        & svg * {
            fill: ${({ $theme }) => $theme.itemTextColor};
        }
    }
`;

const LogoLink = styled.a`
    padding: 4px 5px;
`;

const SideNavHeader = ({
    logoHref = '/',
    autoCollapseWidth,
    onLogoClick,
    hidePinButton: hidePinButtonProp = false
}: ISideNavHeaderProps) => {
    const { isExpanded, isPinned, isHovered, togglePinned } = useSideDrawer();
    const { theme } = useSideNavContext();

    const [hidePinButtonOnNarrow, setHidePinButtonOnNarrow] = useState(false);
    const togglePinnedRef = useRef(togglePinned);
    togglePinnedRef.current = togglePinned;
    const isPinnedRef = useRef(isPinned);
    isPinnedRef.current = isPinned;

    // Remembers whether the drawer was pinned right before the resize
    // observer auto-collapsed it, so growing back above autoCollapseWidth
    // can restore only what this effect itself changed — not override a
    // pin/unpin the consumer made manually in between (e.g. via
    // useSideNavState().togglePinned() while narrow).
    const wasPinnedBeforeAutoCollapseRef = useRef(false);
    // Tracks which side of the threshold the last check landed on, so the
    // collapse/restore action fires exactly once per crossing. A slow drag
    // fires the observer many times while staying on the same side of the
    // threshold — without this guard, a later "still narrow" firing would
    // re-read the already-collapsed isPinned (now false) and clobber
    // wasPinnedBeforeAutoCollapseRef back to false before the drawer ever
    // grows back past the threshold, silently breaking the restore.
    const wasBelowThresholdRef = useRef(false);

    useEffect(() => {
        if (!autoCollapseWidth) {
            return;
        }

        const Observer = new ResizeObserver(() => {
            const isBelowThreshold = window.innerWidth < autoCollapseWidth;
            if (isBelowThreshold === wasBelowThresholdRef.current) {
                return;
            }
            wasBelowThresholdRef.current = isBelowThreshold;

            if (isBelowThreshold) {
                wasPinnedBeforeAutoCollapseRef.current = isPinnedRef.current;
                if (isPinnedRef.current) {
                    togglePinnedRef.current({ forceClose: true });
                }
                setHidePinButtonOnNarrow(true);
            } else {
                if (
                    wasPinnedBeforeAutoCollapseRef.current &&
                    !isPinnedRef.current
                ) {
                    togglePinnedRef.current();
                    wasPinnedBeforeAutoCollapseRef.current = false;
                }
                setHidePinButtonOnNarrow(false);
            }
        });
        Observer.observe(document.body);
        return () => Observer.disconnect();
    }, [autoCollapseWidth]);

    const handlePinClick = () => {
        if (isPinned) {
            togglePinned({ forceClose: true });
        } else {
            togglePinned();
        }
    };

    return (
        <Header>
            <LogoLink
                href={logoHref}
                data-testid="logo"
                onClick={onLogoClick}
                aria-label={'Go to Home'}
            >
                <HBLogo color={colors.white} type="monogram" size="sm" />
            </LogoLink>
            {isExpanded && !hidePinButtonOnNarrow && !hidePinButtonProp && (
                <PinButton
                    $theme={theme}
                    data-state={isPinned ? 'pinned' : 'unpinned'}
                    type="button"
                    tabIndex={!isExpanded || !isPinned ? '-1' : '0'}
                    testIdName="pin-button"
                    icon={ArrowRightLineThin16}
                    label="pin"
                    onClick={handlePinClick}
                    buttonTheme="light-invisible"
                    isHovered={isHovered}
                    isPinned={isPinned}
                    tooltip={isPinned ? 'Minimize menu' : 'Expand menu'}
                    tooltipProps={{
                        placement: 'right',
                        offsetX: 24,
                        offsetY: 0,
                        openOnFocus: false
                    }}
                />
            )}
        </Header>
    );
};

export default SideNavHeader;
